import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useTracks } from "@/context/TrackContext";
import { MessageCircleQuestion, X, Send, Sparkles } from "lucide-react";
import ReactMarkdown from "react-markdown";

const STARTERS = [
  "What are the Four Moves?",
  "How do I handle \"I need to speak with my partner\"?",
  "Walk me through the six call stages",
  "What's the pre-frame script?",
];

function sessionId() {
  let id = localStorage.getItem("psa_bot_session");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("psa_bot_session", id);
  }
  return id;
}

export const SupportBot = () => {
  const { activeTrack } = useTracks();
  const botName = activeTrack?.bot_name || "Support Bot";
  const starters = activeTrack?.starters?.length ? activeTrack.starters : STARTERS;
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (open && !loaded) {
      api.get(`/support-bot/history/${sessionId()}`)
        .then((r) => setMessages(r.data)).catch(() => {}).finally(() => setLoaded(true));
    }
  }, [open, loaded]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);

  const ask = async (text) => {
    const q = (text || input).trim();
    if (!q || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: q }]);
    setBusy(true);
    try {
      const { data } = await api.post("/support-bot/chat", { session_id: sessionId(), message: q });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry — something went wrong. Try again." }]);
    } finally { setBusy(false); }
  };

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="support-bot-fab"
                className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[color:var(--brand-primary)] text-white shadow-xl flex items-center justify-center hover:scale-105 transition-transform">
          <MessageCircleQuestion size={22} strokeWidth={1.5} />
        </button>
      )}

      {open && (
        <div className="fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-2rem)] h-[560px] max-h-[calc(100vh-6rem)] bg-white border-2 border-[color:var(--brand-primary)] shadow-2xl flex flex-col" data-testid="support-bot-panel">
          <div className="bg-[color:var(--brand-primary)] text-white px-5 py-4 flex items-center justify-between">
            <div>
              <div className="text-sm font-medium flex items-center gap-2"><Sparkles size={14} /> {botName}</div>
              <div className="text-[11px] text-white/60 mt-0.5">Trained on your full curriculum</div>
            </div>
            <button onClick={() => setOpen(false)} className="text-white/70 hover:text-white" data-testid="support-bot-close"><X size={18} /></button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="support-bot-messages">
            {messages.length === 0 && (
              <div className="space-y-3">
                <p className="text-sm text-neutral-500">Ask me anything from your training — scripts, obstacles, call stages, frameworks.</p>
                {starters.map((s) => (
                  <button key={s} onClick={() => ask(s)} data-testid="support-bot-starter"
                          className="block w-full text-left text-xs border hairline px-3 py-2.5 hover:border-[color:var(--brand-primary)] transition-colors">
                    {s}
                  </button>
                ))}
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`text-sm leading-relaxed ${m.role === "user"
                ? "ml-8 bg-[color:var(--brand-primary)] text-white px-3 py-2.5"
                : "mr-4 bg-[color:var(--brand-surface2)] px-3 py-2.5"}`}>
                {m.role === "assistant"
                  ? <div className="prose prose-sm max-w-none [&>*]:my-1"><ReactMarkdown>{m.content}</ReactMarkdown></div>
                  : m.content}
              </div>
            ))}
            {busy && <div className="text-xs text-neutral-400">Checking the curriculum…</div>}
            <div ref={bottomRef} />
          </div>

          <div className="border-t hairline p-3 flex items-end gap-2">
            <textarea rows={1} value={input} onChange={(e) => setInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(); } }}
                      placeholder="Ask the curriculum…" data-testid="support-bot-input"
                      className="flex-1 text-sm border hairline px-3 py-2 resize-none focus:outline-none focus:border-[color:var(--brand-primary)]" />
            <button onClick={() => ask()} disabled={busy || !input.trim()} data-testid="support-bot-send"
                    className="w-9 h-9 bg-[color:var(--brand-primary)] text-white flex items-center justify-center disabled:opacity-40">
              <Send size={14} />
            </button>
          </div>
        </div>
      )}
    </>
  );
};
